import { useChat } from '@ai-sdk/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isToolUIPart } from 'ai';
import { Bot, Loader2, MessageSquare, Square } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  Conversation,
  ConversationContent,
  ConversationEmptyState,
  ConversationScrollButton,
} from '@/components/ai-elements';
import { ChatInput, type PendingFile } from '@/components/miranda/ChatInput';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { toFocusPayload, type MirandaFocus } from '@/lib/miranda-focus';
import { cn } from '@/lib/utils';
import {
  createMirandaTransport,
  mirandaRichApi,
  type MirandaUIMessage,
} from '@/services/miranda-rich-chat';

import { isWriteToolResult } from './proposal-card';
import { RichMessage } from './rich-message';

export interface RichChatProps {
  agentId: string;
  agentName?: string;
  agentDescription?: string | null;
  /** La sessió oberta en muntar. Canviar de conversa és tornar a muntar (`key`), no canviar això. */
  sessionId: string | null;
  initialInput?: string;
  /** El servidor ha dit quina sessió és (la primera resposta d'una conversa nova). */
  onSessionId: (sessionId: string) => void;
  /** Un torn s'ha acabat (bé, amb error o aturat). */
  onTurnFinished?: () => void;
  /**
   * El que l'usuari mira (giny lateral): viatja al cos de cada missatge tal com estigui en el
   * moment d'enviar. `null` (cap fitxa o xip descartat) o absent: el cos no porta `focus`.
   */
  focus?: MirandaFocus | null;
  /** Els tokens d'un torn acabat, si el backend els ha dit (`metadata.usage.total`). */
  onUsage?: (total: number) => void;
  /** L'historial de la sessió no s'ha pogut carregar (p. ex. un 404: la sessió ja no existeix). */
  onHistoryError?: (error: unknown) => void;
  /** Per a una columna estreta (el giny, 340-380 px): menys marge i un estat buit més petit. */
  compact?: boolean;
}

/** Un torn ha escrit de debò: el calendari, els llistats i la fitxa oberta s'han de refrescar. */
const QUERIES_AFTER_WRITE = [['calendar-bookings'], ['reservations'], ['reservation-by-beds24']] as const;

/** El text d'un error de l'stream: el `message` del JSON del backend, si ho és. */
export function chatErrorText(error: Error | undefined): string {
  if (!error?.message) return 'No s\'ha pogut connectar amb Miranda.';
  try {
    const parsed = JSON.parse(error.message) as { message?: unknown };
    if (typeof parsed.message === 'string') return parsed.message;
    if (Array.isArray(parsed.message)) return parsed.message.join(' · ');
  } catch {
    // No és JSON: és el missatge tal qual.
  }
  return error.message;
}

/** Un torn ha escrit alguna cosa de debò: cal refrescar el calendari i els llistats. */
export function hasExecutedWrite(message: MirandaUIMessage | undefined): boolean {
  return (message?.parts ?? []).some(
    (part) =>
      isToolUIPart(part) &&
      part.state === 'output-available' &&
      isWriteToolResult(part.output) &&
      part.output.kind === 'executed',
  );
}

/**
 * El xat ric de Miranda (`plans/miranda-mes-senzilla.md`, peça 4): `useChat` de l'AI SDK contra
 * `POST /agent/chat/:agentId/ui`. Es munta a `/chat` i al giny lateral (`MirandaSheet`, amb
 * `compact` i `focus`), i només per a l'agent `miranda`; la resta d'agents segueixen amb el
 * client de text d'avui.
 *
 * Primer carrega l'historial de la sessió (ja filtrat pel backend) i després munta el xat: els
 * missatges inicials de `useChat` només es llegeixen en crear-lo.
 */
export function RichChat(props: RichChatProps) {
  // La sessió de quan s'ha muntat: la que arriba després (la d'una conversa nova) no ha de
  // tornar a carregar res, o es perdria el torn que s'està pintant.
  const [initialSessionId] = useState(props.sessionId);

  const history = useQuery({
    queryKey: ['chat-session-ui', initialSessionId],
    queryFn: () => mirandaRichApi.getSessionUi(initialSessionId as string),
    enabled: !!initialSessionId,
    // Sempre de nou: el que s'ha parlat en viu no és a la còpia desada per la consulta.
    gcTime: 0,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });

  // Qui munta el xat decideix què fer amb una sessió que no es pot llegir (el giny en comença una
  // de nova amb un 404). Un cop per error, no a cada render.
  const onHistoryErrorRef = useRef(props.onHistoryError);
  useEffect(() => {
    onHistoryErrorRef.current = props.onHistoryError;
  }, [props.onHistoryError]);
  useEffect(() => {
    if (history.isError) onHistoryErrorRef.current?.(history.error);
  }, [history.isError, history.error]);

  // `isPending` i no `isLoading`: amb la petició en pausa (sense xarxa) `isLoading` és fals, el
  // xat es muntaria buit i `useChat` ja no llegiria l'historial quan arribés.
  if (initialSessionId && history.isPending) {
    return (
      <div className="flex flex-1 items-center justify-center gap-2 text-muted-foreground text-sm">
        <Loader2 className="size-4 animate-spin" />
        Carregant la conversa…
      </div>
    );
  }

  return (
    <RichChatSession
      {...props}
      initialMessages={history.data?.messages ?? []}
      historyError={history.isError}
    />
  );
}

function RichChatSession({
  agentId,
  agentName = 'Miranda',
  agentDescription,
  sessionId,
  initialInput = '',
  onSessionId,
  onTurnFinished,
  focus,
  onUsage,
  compact = false,
  initialMessages,
  historyError,
}: RichChatProps & { initialMessages: MirandaUIMessage[]; historyError: boolean }) {
  const queryClient = useQueryClient();
  const [input, setInput] = useState(initialInput);
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);

  // La sessió, el focus i els avisos es llegeixen en el moment d'enviar o de rebre, no quan es va
  // crear el transport: la sessió d'una conversa nova arriba a mig primer torn, i amb la fitxa
  // oberta es pot canviar de reserva mentre s'escriu.
  const sessionIdRef = useRef(sessionId);
  const focusRef = useRef(focus);
  const callbacksRef = useRef({ onSessionId, onTurnFinished, onUsage });
  useEffect(() => {
    focusRef.current = focus;
  }, [focus]);
  useEffect(() => {
    callbacksRef.current = { onSessionId, onTurnFinished, onUsage };
  }, [onSessionId, onTurnFinished, onUsage]);

  const transport = useMemo(
    () =>
      createMirandaTransport({
        agentId,
        getSessionId: () => sessionIdRef.current,
        onSessionId: (id) => {
          if (id === sessionIdRef.current) return;
          sessionIdRef.current = id;
          callbacksRef.current.onSessionId(id);
        },
        getFocus: () => toFocusPayload(focusRef.current),
      }),
    [agentId],
  );

  const { messages, sendMessage, status, stop, error, clearError } = useChat<MirandaUIMessage>({
    transport,
    messages: initialMessages,
    onFinish: ({ message }) => {
      if (hasExecutedWrite(message)) {
        for (const queryKey of QUERIES_AFTER_WRITE) {
          void queryClient.invalidateQueries({ queryKey: [...queryKey] });
        }
      }
      const total = message.metadata?.usage?.total;
      if (typeof total === 'number' && total > 0) callbacksRef.current.onUsage?.(total);
      callbacksRef.current.onTurnFinished?.();
    },
  });

  const busy = status === 'submitted' || status === 'streaming';
  const lastMessage = messages[messages.length - 1];
  // Enviat i encara sense cap part de la resposta: el «pensant…».
  const waiting = busy && (lastMessage?.role !== 'assistant' || lastMessage.parts.length === 0);

  const handleSubmit = (event?: React.FormEvent) => {
    event?.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    const files = pendingFiles.map(({ dataUri, name, mimeType }) => ({ dataUri, name, mimeType }));
    setInput('');
    setPendingFiles([]);
    if (error) clearError();
    void sendMessage({ text }, files.length > 0 ? { body: { files } } : undefined);
  };

  return (
    // Res del xat pot ser més ample que la seva columna: tota la cadena de contenidors flex és
    // `min-w-0`, i el que és ample de mena (una taula) fa scroll dins de la seva bombolla.
    <div className="flex min-h-0 min-w-0 max-w-full flex-1 flex-col" data-testid="rich-chat">
      <Conversation className="min-h-0 min-w-0 max-w-full">
        <ConversationContent className={cn('min-w-0 max-w-full break-words', compact ? 'gap-3 p-3' : 'gap-4')}>
          {historyError && (
            <Alert variant="destructive">
              <AlertDescription>
                No s'ha pogut carregar l'historial d'aquesta conversa. Pots seguir escrivint: Miranda sí que el té.
              </AlertDescription>
            </Alert>
          )}

          {messages.length === 0 && !busy && !historyError && (
            <ConversationEmptyState
              className={cn(compact && 'gap-2 p-4')}
              icon={<MessageSquare className={compact ? 'size-8 opacity-40' : 'size-12'} />}
              title={`Hola! Sóc ${agentName}`}
              description={agentDescription || 'Com et puc ajudar?'}
            />
          )}

          {messages.map((message) => (
            // Mentre l'últim missatge s'està pintant, les seves targetes no deixen decidir res.
            <RichMessage key={message.id} message={message} isStreaming={busy && message.id === lastMessage?.id} />
          ))}

          {waiting && (
            <div className="flex items-center gap-2 text-muted-foreground text-sm" role="status">
              <Bot className="size-4 text-primary" />
              <Loader2 className="size-4 animate-spin" />
              {agentName} està pensant…
            </div>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{chatErrorText(error)}</AlertDescription>
            </Alert>
          )}
        </ConversationContent>
        <ConversationScrollButton />
      </Conversation>

      <div className={cn('min-w-0 max-w-full space-y-2 border-t', compact ? 'p-3' : 'p-4')}>
        {busy && (
          <div className="flex justify-end">
            {/* `stop()` avorta el `fetch`: el navegador talla la connexió i el backend atura el torn. */}
            <Button type="button" size="sm" variant="outline" onClick={() => void stop()}>
              <Square className="size-3.5" />
              Atura
            </Button>
          </div>
        )}
        {/* `ChatInput` no es toca: l'embolcall és qui no el deixa passar de l'amplada del xat. */}
        <div className="min-w-0 max-w-full">
          <ChatInput
            input={input}
            onInputChange={setInput}
            onSubmit={handleSubmit}
            isLoading={busy}
            placeholder={`Escriu un missatge a ${agentName}...`}
            pendingFiles={pendingFiles}
            onAddFiles={(files) => setPendingFiles((prev) => [...prev, ...files])}
            onRemoveFile={(id) => setPendingFiles((prev) => prev.filter((file) => file.id !== id))}
          />
        </div>
      </div>
    </div>
  );
}
